import SearchableFilterList from './SearchableFilterList';
import useFieldValidation, { isBlank } from './useFieldValidation';
import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, House, Image, MapPin, Users, X } from 'lucide-react';

const DEFAULT_DIAL_ISO = 'IN';

export const DIAL_COUNTRIES = [
  { iso: 'AE', name: 'United Arab Emirates', dial: '+971' },
  { iso: 'AU', name: 'Australia', dial: '+61' },
  { iso: 'BE', name: 'Belgium', dial: '+32' },
  { iso: 'CN', name: 'China', dial: '+86' },
  { iso: 'DE', name: 'Germany', dial: '+49' },
  { iso: 'ES', name: 'Spain', dial: '+34' },
  { iso: 'FR', name: 'France', dial: '+33' },
  { iso: 'GB', name: 'United Kingdom', dial: '+44' },
  { iso: 'GR', name: 'Greece', dial: '+30' },
  { iso: 'IN', name: 'India', dial: '+91' },
  { iso: 'IT', name: 'Italy', dial: '+39' },
  { iso: 'KR', name: 'South Korea', dial: '+82' },
  { iso: 'NL', name: 'Netherlands', dial: '+31' },
  { iso: 'PT', name: 'Portugal', dial: '+351' },
  { iso: 'SA', name: 'Saudi Arabia', dial: '+966' },
  { iso: 'SG', name: 'Singapore', dial: '+65' },
  { iso: 'US', name: 'United States', dial: '+1' },
].sort((a, b) => a.name.localeCompare(b.name));

const DIAL_BY_LENGTH = [...DIAL_COUNTRIES].sort((a, b) => b.dial.length - a.dial.length);

export function CountryFlag({ iso }) {
  const code = String(iso || DEFAULT_DIAL_ISO).toLowerCase();
  return <img className="cu-phone-flag-img" src={`https://flagcdn.com/w40/${code}.png`} alt="" width="22" height="16" />;
}

export function countryByIso(iso) {
  return DIAL_COUNTRIES.find(item => item.iso === iso) || DIAL_COUNTRIES.find(item => item.iso === DEFAULT_DIAL_ISO);
}

export function parseMobile(value) {
  const compact = String(value || '').trim().replace(/[().-]/g, ' ').replace(/\s+/g, ' ');
  if (!compact) return { iso: DEFAULT_DIAL_ISO, national: '' };
  if (compact.startsWith('+')) {
    const match = DIAL_BY_LENGTH.find(country => (
      compact === country.dial
      || compact.startsWith(`${country.dial} `)
      || (compact.startsWith(country.dial) && /\d/.test(compact.charAt(country.dial.length)))
    ));
    if (match) return { iso: match.iso, national: compact.slice(match.dial.length).trim() };
  }
  return { iso: DEFAULT_DIAL_ISO, national: compact.replace(/^\+/, '') };
}

export function composeMobile(iso, national) {
  const number = String(national || '').replace(/[^\d\s]/g, '').replace(/\s+/g, ' ').trim();
  if (!number) return '';
  const country = countryByIso(iso);
  return `${country.dial} ${number}`.slice(0, 50);
}

function sanitizeNational(iso, value) {
  if (iso === 'IN') {
    const digits = String(value || '').replace(/\D/g, '').slice(0, 10);
    return digits.length > 5 ? `${digits.slice(0, 5)} ${digits.slice(5)}` : digits;
  }
  return String(value || '').replace(/[^\d\s]/g, '').replace(/\s+/g, ' ').replace(/^\s/, '').slice(0, 20);
}

const emptyForm = {
  name: '',
  dialIso: DEFAULT_DIAL_ISO,
  nationalNumber: '',
  address: '',
  logo: '',
  logoName: '',
};

const MAX_LOGO_BYTES = 1024 * 1024;
const ALLOWED_LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];

const IGNORED_INITIAL_WORDS = new Set([
  '&', 'and', 'the', 'of', 'nv', 'ag', 'ltd', 'sl', 'fze', 'inc', 'llc', 'bv',
  'gmbh', 'sa', 'sas', 'plc', 'co', 'corp', 'limited', 'pty', 'lp', 'llp', 'srl', 'spa',
]);

export function customerInitials(name) {
  const words = String(name || '').split(/[\s,/.-]+/).filter(word => {
    const cleaned = word.replace(/[.]/g, '').toLowerCase();
    return cleaned && !IGNORED_INITIAL_WORDS.has(cleaned);
  });
  const letters = words.slice(0, 2).map(word => word[0]).join('').toUpperCase();
  if (letters) return letters;
  const fallback = String(name || '').replace(/[^A-Za-z0-9]/g, '');
  return (fallback.slice(0, 2) || '?').toUpperCase();
}

export function CustomerLogo({ name, logo }) {
  if (logo) {
    return <span className="cu-logo"><img src={logo} alt="" /></span>;
  }
  return <span className="cu-logo" aria-hidden="true">{String(name || '').trim() ? customerInitials(name) : ''}</span>;
}

function formFromCustomer(customer) {
  if (!customer) return emptyForm;
  const parsed = parseMobile(customer.mobile_number);
  return {
    name: customer.name || '',
    dialIso: parsed.iso,
    nationalNumber: parsed.national,
    address: customer.address || '',
    logo: customer.logo || '',
    logoName: '',
  };
}

function validateForm(form) {
  const errors = {};
  if (isBlank(form.name)) errors.name = 'Customer Name is required.';
  if (form.name.trim().length > 200) errors.name = 'Customer Name must be 200 characters or fewer.';
  if (composeMobile(form.dialIso, form.nationalNumber).length > 50) errors.mobile_number = 'Mobile Number must be 50 characters or fewer.';
  return errors;
}

function readLogoFile(file) {
  if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
    return Promise.reject(new Error('Upload a PNG, JPG, WEBP, or GIF image.'));
  }
  if (file.size > MAX_LOGO_BYTES) {
    return Promise.reject(new Error('Logo must be 1 MB or smaller.'));
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('The logo file could not be read.'));
    reader.readAsDataURL(file);
  });
}

export default function CustomerModal({ mode, customer, onClose, onSubmit }) {
  const isEdit = mode === 'edit';
  const [form, setForm] = useState(() => formFromCustomer(customer));
  const { errors, setErrors, touchField } = useFieldValidation(form, validateForm);
  const [saving, setSaving] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [dialOpen, setDialOpen] = useState(false);
  const fileInput = useRef(null);
  const dialRef = useRef(null);
  const titleId = 'cu-form-title';
  const selectedCountry = countryByIso(form.dialIso);

  useEffect(() => {
    setForm(formFromCustomer(customer));
    setErrors({});
    setDragging(false);
    setDialOpen(false);
    if (fileInput.current) fileInput.current.value = '';
  }, [mode, customer, setErrors]);

  useEffect(() => {
    function handleKey(event) {
      if (event.key !== 'Escape' || saving) return;
      if (dialOpen) {
        event.stopPropagation();
        setDialOpen(false);
        return;
      }
      onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose, saving, dialOpen]);

  useEffect(() => {
    if (!dialOpen) return undefined;
    function handlePointer(event) {
      if (!dialRef.current?.contains(event.target)) setDialOpen(false);
    }
    document.addEventListener('mousedown', handlePointer);
    return () => document.removeEventListener('mousedown', handlePointer);
  }, [dialOpen]);

  function updateField(name, value) {
    setForm(current => ({ ...current, [name]: value }));
    setErrors(current => ({ ...current, [name]: undefined }));
  }

  async function applyLogoFile(file) {
    if (!file) return;
    try {
      const logo = await readLogoFile(file);
      setForm(current => ({ ...current, logo, logoName: file.name }));
      setErrors(current => ({ ...current, logo: undefined }));
    } catch (error) {
      if (fileInput.current) fileInput.current.value = '';
      setErrors(current => ({ ...current, logo: error.message }));
    }
  }

  function handleLogoChange(event) {
    applyLogoFile(event.target.files?.[0]);
  }

  function handleDragOver(event) {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setDragging(true);
  }

  function handleDragLeave(event) {
    if (!event.currentTarget.contains(event.relatedTarget)) setDragging(false);
  }

  function handleDrop(event) {
    event.preventDefault();
    setDragging(false);
    applyLogoFile(event.dataTransfer.files?.[0]);
  }

  function clearLogo() {
    setForm(current => ({ ...current, logo: '', logoName: '' }));
    setErrors(current => ({ ...current, logo: undefined }));
    if (fileInput.current) fileInput.current.value = '';
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (saving) return;
    const nextErrors = validateForm(form);
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    setSaving(true);
    try {
      await onSubmit({
        name: form.name.trim(),
        mobile_number: composeMobile(form.dialIso, form.nationalNumber),
        address: form.address.trim(),
        logo: form.logo.trim(),
      });
      onClose();
    } catch (error) {
      setErrors({ form: error.message });
    } finally {
      setSaving(false);
    }
  }

  return <div className="wh-modal-backdrop" onMouseDown={event => { if (event.target === event.currentTarget && !saving) onClose(); }}>
    <form className="wh-form-modal" role="dialog" aria-modal="true" aria-labelledby={titleId} onSubmit={handleSubmit}>
      <header className="wh-form-head">
        <span className="wh-form-head-icon" aria-hidden="true"><Users size={18} strokeWidth={1.8} /></span>
        <div>
          <h2 id={titleId}>{isEdit ? 'Edit Customer' : 'Add Customer'}</h2>
          <p>{isEdit ? 'Update customer details' : 'Add a new customer record'}</p>
        </div>
        <button className="wh-form-close" type="button" aria-label="Close" onClick={onClose} disabled={saving}><X size={16} strokeWidth={1.8} /></button>
      </header>

      <div className="wh-form-body">
        <label className="wh-form-field" htmlFor="cu-form-name">
          <span>Customer Name <em>*</em></span>
          <span className={`wh-form-control${errors.name ? ' invalid' : ''}`}>
            <House size={16} strokeWidth={1.7} />
            <input onBlur={() => touchField('name')}  id="cu-form-name" value={form.name} placeholder="Enter customer name" aria-invalid={Boolean(errors.name)} autoFocus onChange={event => updateField('name', event.target.value)} />
          </span>
          {errors.name && <small className="wh-form-error">{errors.name}</small>}
        </label>

        <div className="wh-form-field">
          <label htmlFor="cu-form-mobile">Mobile Number</label>
          <div className={`cu-phone${errors.mobile_number ? ' invalid' : ''}${dialOpen ? ' open' : ''}`}>
            <div className="cu-phone-dial" ref={dialRef} onBlur={event => {
              if (!event.currentTarget.contains(event.relatedTarget)) setDialOpen(false);
            }}>
              <button
                className={`cu-phone-trigger${dialOpen ? ' open' : ''}`}
                type="button"
                aria-haspopup="listbox"
                aria-expanded={dialOpen}
                aria-label={`Country code ${selectedCountry.dial}`}
                data-open-on-focus
                onMouseDown={event => { if (!dialOpen) event.preventDefault(); }}
                onFocus={event => { if (!dialRef.current?.contains(event.relatedTarget)) setDialOpen(true); }}
                onClick={() => setDialOpen(open => !open)}
              >
                <span className="cu-phone-flag" aria-hidden="true"><CountryFlag iso={selectedCountry.iso} /></span>
                <span className="cu-phone-code">{selectedCountry.dial}</span>
                <ChevronDown size={14} strokeWidth={2} className="wh-form-select-caret" />
              </button>
              {dialOpen && <div className="cu-phone-menu" role="listbox"><SearchableFilterList label="country codes">
                {DIAL_COUNTRIES.map(country => (
                  <button
                    key={country.iso}
                    className={`wh-form-option${form.dialIso === country.iso ? ' selected' : ''}`}
                    type="button"
                    role="option"
                    aria-selected={form.dialIso === country.iso}
                    onClick={() => {
                      setForm(current => ({
                        ...current,
                        dialIso: country.iso,
                        nationalNumber: sanitizeNational(country.iso, current.nationalNumber),
                      }));
                      setErrors(current => ({ ...current, mobile_number: undefined }));
                      dialRef.current?.querySelector('button[aria-haspopup="listbox"]')?.focus({ preventScroll: true });
                      setDialOpen(false);
                    }}
                  >
                    <span className="cu-phone-flag" aria-hidden="true"><CountryFlag iso={country.iso} /></span>
                    <span className="cu-phone-option-name">{country.name}</span>
                    <span className="cu-phone-option-dial">{country.dial}</span>
                  </button>
                ))}
              </SearchableFilterList></div>}
            </div>
            <span className="cu-phone-divider" aria-hidden="true" />
            <input onBlur={() => touchField('nationalNumber')}
              id="cu-form-mobile"
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              value={form.nationalNumber}
              placeholder="Enter mobile number"
              aria-invalid={Boolean(errors.mobile_number)}
              onChange={event => updateField('nationalNumber', sanitizeNational(form.dialIso, event.target.value))}
            />
          </div>
          {errors.mobile_number && <small className="wh-form-error">{errors.mobile_number}</small>}
        </div>

        <label className="wh-form-field" htmlFor="cu-form-address">
          <span>Address</span>
          <span className={`wh-form-control${errors.address ? ' invalid' : ''}`}>
            <MapPin size={16} strokeWidth={1.7} />
            <input onBlur={() => touchField('address')}  id="cu-form-address" value={form.address} placeholder="Enter address" aria-invalid={Boolean(errors.address)} onChange={event => updateField('address', event.target.value)} />
          </span>
          {errors.address && <small className="wh-form-error">{errors.address}</small>}
        </label>

        <div className="wh-form-field">
          <label htmlFor="cu-form-logo">Logo</label>
          <div
            className={`cu-dropzone${dragging ? ' dragging' : ''}${form.logo ? ' has-file' : ''}${errors.logo ? ' invalid' : ''}`}
            onDragEnter={handleDragOver}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              ref={fileInput}
              id="cu-form-logo"
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              aria-invalid={Boolean(errors.logo)}
              onChange={handleLogoChange}
            />
            {form.logo
              ? <>
                <CustomerLogo name={form.name} logo={form.logo} />
                <span className="cu-dropzone-copy">
                  <strong>{form.logoName || 'Current logo'}</strong>
                  <small>PNG, JPG, WEBP or GIF (Max 1 MB)</small>
                </span>
                <button className="cu-file-clear" type="button" onClick={event => { event.preventDefault(); clearLogo(); }}>Remove</button>
              </>
              : <>
                <Image className="cu-dropzone-icon" size={22} strokeWidth={1.6} />
                <span className="cu-dropzone-copy">
                  <strong>Choose file or drag and drop</strong>
                  <small>PNG, JPG, WEBP or GIF (Max 1 MB)</small>
                </span>
              </>}
          </div>
          {errors.logo && <small className="wh-form-error">{errors.logo}</small>}
        </div>

        {errors.form && <p className="wh-modal-error" role="alert">{errors.form}</p>}
      </div>

      <footer className="wh-form-footer">
        <button className="wh-form-cancel" type="button" onClick={onClose} disabled={saving}>Cancel</button>
        <button className="wh-form-save" type="submit" disabled={saving}>
          {saving ? 'Saving...' : <><Check size={15} strokeWidth={2.2} />{isEdit ? 'Save Changes' : 'Save Customer'}</>}
        </button>
      </footer>
    </form>
  </div>;
}
